/**
 * Navigation Controller Service
 *
 * Manages navigation state and provides abort signals for pages to cancel
 * ongoing operations when the user navigates away.
 *
 * Usage:
 * 1. At the start of page loading: const signal = navigationController.startPageLoad('pageName');
 * 2. Check before async operations: if (navigationController.isAborted('pageName')) return;
 * 3. Pass signal to fetch calls: fetch(url, { signal })
 * 4. On navigation: automatically aborts previous page's operations
 */

class NavigationController {
  constructor() {
    // Current page being loaded
    this.currentPage = null;

    // AbortController for current page
    this.abortController = null;

    // Timestamp of last navigation
    this.lastNavigationTime = 0;

    // Callbacks to run on abort
    this.abortCallbacks = new Map();

    // Setup navigation listener
    this._setupNavigationListener();
  }

  /**
   * Setup listener for navigation events
   */
  _setupNavigationListener() {
    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', () => {
      this._handleNavigation();
    });

    // Listen for custom navigation event
    window.addEventListener('app-navigation', () => {
      this._handleNavigation();
    });
  }

  /**
   * Handle navigation - abort current page loading
   */
  _handleNavigation() {
    this.abortCurrentPage();
  }

  /**
   * Start loading a new page - aborts any previous page loading
   * @param {string} pageName - Identifier for the page
   * @returns {AbortSignal} - Signal to pass to fetch calls
   */
  startPageLoad(pageName) {
    // Abort previous page if any
    if (this.currentPage && this.currentPage !== pageName) {
      this.abortCurrentPage();
    }

    // Create new AbortController for this page
    this.abortController = new AbortController();
    this.currentPage = pageName;
    this.lastNavigationTime = Date.now();

    return this.abortController.signal;
  }

  /**
   * Abort current page loading
   */
  abortCurrentPage() {
    if (this.abortController) {
      this.abortController.abort();

      // Run any registered abort callbacks
      const callbacks = this.abortCallbacks.get(this.currentPage);
      if (callbacks) {
        callbacks.forEach(cb => {
          try {
            cb();
          } catch (e) {
            // silent
          }
        });
        this.abortCallbacks.delete(this.currentPage);
      }
    }

    this.abortController = null;
    this.currentPage = null;
  }

  /**
   * Check if the current page load was aborted
   * @param {string} pageName - The page to check
   * @returns {boolean} - True if aborted or different page is loading
   */
  isAborted(pageName) {
    // If different page is loading, consider this one aborted
    if (this.currentPage !== pageName) {
      return true;
    }

    // Check if abort was signaled
    return this.abortController?.signal?.aborted || false;
  }

  /**
   * Get the current abort signal
   * @returns {AbortSignal|null}
   */
  getSignal() {
    return this.abortController?.signal || null;
  }

  /**
   * Register a callback to run when page is aborted
   * @param {string} pageName - The page name
   * @param {Function} callback - Callback to run on abort
   */
  onAbort(pageName, callback) {
    if (!this.abortCallbacks.has(pageName)) {
      this.abortCallbacks.set(pageName, []);
    }
    this.abortCallbacks.get(pageName).push(callback);
  }

  /**
   * Clear abort callbacks for a page
   * @param {string} pageName - The page name
   */
  clearAbortCallbacks(pageName) {
    this.abortCallbacks.delete(pageName);
  }

  /**
   * Check if a specific page is currently loading
   * @param {string} pageName - The page to check
   * @returns {boolean}
   */
  isPageLoading(pageName) {
    return this.currentPage === pageName && !this.isAborted(pageName);
  }

  /**
   * Mark page loading as complete
   * @param {string} pageName - The page that finished loading
   */
  finishPageLoad(pageName) {
    if (this.currentPage === pageName) {
      this.clearAbortCallbacks(pageName);
      // Don't clear currentPage - keep it for reference
    }
  }

  /**
   * Helper to wrap async operations with abort check
   * @param {string} pageName - The page name
   * @param {Function} asyncFn - Async function to run
   * @returns {Promise} - Resolves with result or rejects if aborted
   */
  async runIfNotAborted(pageName, asyncFn) {
    if (this.isAborted(pageName)) {
      throw new Error('Navigation aborted');
    }

    const result = await asyncFn();

    if (this.isAborted(pageName)) {
      throw new Error('Navigation aborted');
    }

    return result;
  }
}

// Export singleton instance
export const navigationController = new NavigationController();
